import React, { useMemo } from 'react'
import { Stack, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { FragmentType, useFragment } from '@/generated/gql'
import { FilterChip } from './FilterChip'
import { canteenMenuFiltersPanelVendorMenuDataFragmentV2 } from './FiltersPanelMuiV2'
import type { FilterCategory } from './types'

type Props = {
  menuData?: FragmentType<typeof canteenMenuFiltersPanelVendorMenuDataFragmentV2>[]
  menuFilters: {
    dietaries: string[]
    allergens: string[]
    vendors: string[]
  }
  onToggleFilterValue: (category: FilterCategory, value: string) => void
  searchTerm: string
  onClearSearchTerm: () => void
}

export const MobileFiltersList: React.FC<Props> = ({
  menuData: menuDataProp,
  menuFilters,
  onToggleFilterValue,
  searchTerm,
  onClearSearchTerm,
}) => {
  const { t } = useTranslation()
  const menuData = useFragment(canteenMenuFiltersPanelVendorMenuDataFragmentV2, menuDataProp)

  const handleRemoveFilter = (category: string, value: string) => {
    if (category === 'vendors' || category === 'dietaries' || category === 'allergens') {
      onToggleFilterValue(category, value)
    }
  }

  const selectedDietaries = useMemo(() => {
    return menuFilters.dietaries.map((dietary) => {
      return {
        label: t(`dietaries:${dietary}`),
        value: dietary,
        displayName: t(`dietaries:${dietary}`),
      }
    })
  }, [menuFilters.dietaries, t])

  const selectedAllergens = useMemo(() => {
    return menuFilters.allergens.map((allergen) => {
      return {
        label: ['nuts', 'cereals'].includes(allergen) ? t(`dietaries:${allergen}`) : t(`allergens:${allergen}`),
        value: allergen,
      }
    })
  }, [menuFilters.allergens, t])

  const selectedVendors = useMemo(() => {
    return menuFilters.vendors.map((vendorId) => {
      const matchingMenu = menuData?.find((menu) => menu.vendor.id === vendorId)
      return {
        label: matchingMenu ? matchingMenu.vendor.companyName : vendorId,
        value: vendorId,
      }
    })
  }, [menuFilters.vendors, menuData])

  return (
    <Box>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {searchTerm && <FilterChip label={`"${searchTerm}"`} onDelete={onClearSearchTerm} />}
        {[...selectedVendors].map(({ label, value }) => (
          <FilterChip
            key={`filter-chip-${value}`}
            label={label}
            onDelete={() => handleRemoveFilter('vendors', value)}
          />
        ))}
        {[...selectedAllergens].map(({ label, value }) => (
          <FilterChip
            key={`filter-chip-${value}`}
            label={label}
            onDelete={() => handleRemoveFilter('allergens', value)}
          />
        ))}
        {[...selectedDietaries].map(({ label, value }) => (
          <FilterChip
            key={`filter-chip-${value}`}
            label={label}
            onDelete={() => handleRemoveFilter('dietaries', value)}
          />
        ))}
      </Stack>
    </Box>
  )
}
